import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { ThemeProvider } from '@/theme/theme-provider';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';
import { MODULE_ICONS } from '@/modules/dashboard/module-icons';
import { resolveNavItem, useShortcuts } from '@/modules/shortcuts/shortcuts';
import { StarIcon } from '@/modules/shortcuts/star-icon';
import { NAV_SECTIONS, type NavItem, type NavSection } from './nav-sections';

const BellIcon = MODULE_ICONS['/notifications'];
const SecurityIcon = MODULE_ICONS['/security'];
const SettingsIcon = MODULE_ICONS['/settings'];

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-4.35-4.35" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shell-nav-chevron"
      data-open={open || undefined}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/** A framed panel with a divider and a directional chevron — the same
 *  "collapse the rail" glyph most desktop apps use, so it reads correctly
 *  without needing to be looked up. */
function SidebarToggleIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="9" y1="4" x2="9" y2="20" />
      <path d={collapsed ? 'M13 9l3 3-3 3' : 'M15 9l-3 3 3 3'} />
    </svg>
  );
}

const COLLAPSE_STORAGE_KEY = 'tabita.navCollapsedSections';
const SIDEBAR_COLLAPSED_KEY = 'tabita.sidebarCollapsed';

/** Per-viewer only — which sidebar groups they last closed. Never anything
 *  that needs to match another device or survive a cleared browser. */
function readCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeCollapsed(value: Record<string, boolean>): void {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* a private window or full storage just means this resets on reload */
  }
}

/** Whether the whole sidebar is a narrow icon rail — a separate, coarser
 *  preference from which individual sections are open. */
function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(value: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
  } catch {
    /* a private window or full storage just means this resets on reload */
  }
}

/**
 * The application shell.
 *
 * The sidebar is grouped using the same taxonomy the dashboard's module
 * tiles already use (`module-tiles.ts`) — community, communications,
 * finance, system — plus one section, visually set apart, for the handful
 * of routes only a TABITA platform operator ever sees.
 */
export function AppShell() {
  const { t } = useI18n();
  const { can, logout, roles } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed);
  const [navSearch, setNavSearch] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(readSidebarCollapsed);

  function toggleSection(id: string): void {
    setCollapsed((previous) => {
      const next = { ...previous, [id]: !previous[id] };
      writeCollapsed(next);
      return next;
    });
  }

  function toggleSidebar(): void {
    setSidebarCollapsed((previous) => {
      const next = !previous;
      writeSidebarCollapsed(next);
      return next;
    });
  }

  const { shortcuts, isShortcut, toggle: toggleShortcut } = useShortcuts();

  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.permission === null || can(item.permission)),
  })).filter((section) => section.items.length > 0);

  // A synthetic section, not one of `NAV_SECTIONS` — built from whatever the
  // caller has starred, so it only ever shows pages they can still open, and
  // disappears entirely rather than sitting there empty for anyone who
  // hasn't used the feature yet.
  const shortcutItems = shortcuts
    .map((to) => resolveNavItem(to))
    .filter((item): item is NavItem => item !== undefined)
    .filter((item) => item.permission === null || can(item.permission));
  const sectionsWithShortcuts: readonly NavSection[] =
    shortcutItems.length > 0
      ? [{ id: 'shortcuts', labelKey: 'shortcuts.title', items: shortcutItems }, ...visibleSections]
      : visibleSections;

  const navQuery = navSearch.trim().toLowerCase();
  const isSearchingNav = navQuery.length > 0;
  const filteredSections = isSearchingNav
    ? sectionsWithShortcuts
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => t(item.labelKey).toLowerCase().includes(navQuery)),
        }))
        .filter((section) => section.items.length > 0)
    : sectionsWithShortcuts;

  async function handleLogout(): Promise<void> {
    await logout();
    void navigate('/login', { replace: true });
  }

  return (
    <ThemeProvider>
      <div className="shell">
        {/* First focusable element on the page, for keyboard users. */}
        <a href="#main" className="skip-link">
          {t('navigation.dashboard')}
        </a>

        <header className="shell-header">
          <span className="shell-brand">
            <LogoMark size={28} />
            TABITA
          </span>

          {can('search.global.read') && (
            <form
              className="shell-search"
              role="search"
              onSubmit={(event) => {
                event.preventDefault();
                if (searchTerm.trim().length >= 2) {
                  void navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
                }
              }}
            >
              <SearchIcon />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('search.placeholder')}
                aria-label={t('search.placeholder')}
              />
            </form>
          )}

          <div className="shell-actions">
            {can('notification.log.read') && (
              <NavLink
                to="/notifications"
                className="shell-icon-btn"
                aria-label={t('notifications.log.title')}
              >
                {BellIcon && <BellIcon />}
              </NavLink>
            )}
            <LocalePicker />
            <span className="shell-role">{roles[0]}</span>
            <NavLink
              to="/settings"
              className="shell-icon-btn"
              aria-label={t('settings.mySettings.navLabel')}
            >
              {SettingsIcon && <SettingsIcon size={19} />}
            </NavLink>
            <NavLink
              to="/security"
              className="shell-icon-btn"
              aria-label={t('mfa.settings.navLabel')}
            >
              {SecurityIcon && <SecurityIcon size={19} />}
            </NavLink>
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              onClick={() => void handleLogout()}
            >
              {t('auth.signOut')}
            </button>
          </div>
        </header>

        <div className="shell-body">
          <nav
            aria-label={t('navigation.dashboard')}
            className="shell-sidebar"
            data-collapsed={sidebarCollapsed || undefined}
          >
            <div className="shell-nav-top">
              <button
                type="button"
                className="shell-sidebar-toggle"
                onClick={toggleSidebar}
                aria-expanded={!sidebarCollapsed}
                aria-label={
                  sidebarCollapsed ? t('navigation.expandMenu') : t('navigation.collapseMenu')
                }
                title={sidebarCollapsed ? t('navigation.expandMenu') : t('navigation.collapseMenu')}
              >
                <SidebarToggleIcon collapsed={sidebarCollapsed} />
              </button>

              {!sidebarCollapsed && (
                <label className="shell-nav-search">
                  <SearchIcon />
                  <span className="sr-only">{t('navigation.searchMenu')}</span>
                  <input
                    type="search"
                    className="shell-nav-search-input"
                    placeholder={t('navigation.searchMenu')}
                    value={navSearch}
                    onChange={(event) => setNavSearch(event.target.value)}
                    autoComplete="off"
                  />
                </label>
              )}
            </div>

            {!sidebarCollapsed && isSearchingNav && filteredSections.length === 0 && (
              <p className="shell-nav-empty">{t('navigation.noMenuMatches')}</p>
            )}

            {(sidebarCollapsed ? sectionsWithShortcuts : filteredSections).map((section) => {
              const isCollapsed =
                !sidebarCollapsed &&
                !isSearchingNav &&
                section.labelKey !== null &&
                collapsed[section.id];
              return (
                <div key={section.id} className="shell-nav-section" data-variant={section.variant}>
                  {section.labelKey && !sidebarCollapsed && (
                    <button
                      type="button"
                      className="shell-nav-section-header"
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleSection(section.id)}
                    >
                      <span>{t(section.labelKey)}</span>
                      <ChevronIcon open={!isCollapsed} />
                    </button>
                  )}
                  {!isCollapsed && (
                    <div className="shell-nav-list">
                      {section.items.map((item) => {
                        const Icon = MODULE_ICONS[item.to];
                        return (
                          <div className="shell-nav-item" key={item.to}>
                            <NavLink
                              to={item.to}
                              end={item.to === '/' || item.end === true}
                              className={({ isActive }) =>
                                isActive ? 'shell-nav-link is-active' : 'shell-nav-link'
                              }
                              title={sidebarCollapsed ? t(item.labelKey) : undefined}
                            >
                              <span className="shell-nav-icon">{Icon && <Icon size={19} />}</span>
                              <span className={sidebarCollapsed ? 'sr-only' : undefined}>
                                {t(item.labelKey)}
                              </span>
                            </NavLink>
                            {!sidebarCollapsed && (
                              <button
                                type="button"
                                className="shell-nav-star"
                                data-active={isShortcut(item.to) || undefined}
                                onClick={() => toggleShortcut(item.to)}
                                aria-label={
                                  isShortcut(item.to) ? t('shortcuts.remove') : t('shortcuts.add')
                                }
                                title={
                                  isShortcut(item.to) ? t('shortcuts.remove') : t('shortcuts.add')
                                }
                              >
                                <StarIcon filled={isShortcut(item.to)} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <main id="main" className="shell-main">
            <Outlet />
          </main>
        </div>

        <footer className="shell-footer">TABITA</footer>
      </div>
    </ThemeProvider>
  );
}
